import { observer } from 'mobx-react-lite';
import React, { useMemo, useTransition } from 'react';
import { basedAxios } from '../../store/Store';
import cx from 'classnames';
import AttendeeCard from './components/AttendeeCard';
import AttendeeListItem from './components/AttendeeListItem';
import LoadingSkeleton from './components/LoadingSkeleton';
import DisplayTypeToggle from './components/DisplayTypeToggle';
import SortByMenu from './components/SortByMenu';

interface IAttendeeListProps {
  environment: {
    eventId: string;
    lang: string;
  } & unknown;
  settings: {
    displayType: string;
    selection: number[];
    sortBy: string;
    fields: string[];
    hideImage?: boolean;
  };
}

function AttendeeList({ environment, settings }: IAttendeeListProps) {
  const ref = React.useRef(null);
  const [searchTerm, setSearchTerm] = React.useState('');
  const [displayType, setDisplayType] = React.useState('grid');
  const [sortBy, setSortBy] = React.useState('name');
  const [attendeeList, setAttendeeList] = React.useState([]);
  const [isLoading, setIsLoading] = React.useState(false);
  const [isPending, startTransition] = useTransition();

  const { lang } = environment;
  const { fields = [], selection = [], hideImage = false } = settings;

  React.useEffect(() => {
    async function fetchOrders() {
      setIsLoading(true);
      const response = await basedAxios.get('./attendee/getpartialorders/' + environment.eventId);

      setAttendeeList(response.data);
      setIsLoading(false);
    }
    fetchOrders();
  }, []);

  const filteredAttendeeList = attendeeList
    ?.filter((attendee: any) => {
      return (
        attendee.firstName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        attendee.lastName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        attendee.email?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        attendee.telephone?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        attendee.companyName?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        attendee.title?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        attendee?.country?.nameSwe.toLowerCase().includes(searchTerm?.toLowerCase()) ||
        attendee?.country?.nameInt.toLowerCase().includes(searchTerm?.toLowerCase())
      );
    })
    .filter((attendee: any) => (selection?.length > 0 ? attendee?.answerOptionIds?.some((option: any) => selection.includes(option)) : true));

  const sortedAttendeeList = filteredAttendeeList?.sort((a: any, b: any) => {
    if (sortBy === 'name') {
      return a.firstName.localeCompare(b.firstName);
    }
    if (sortBy === 'company') {
      return a.companyName.localeCompare(b.companyName);
    }
    if (sortBy === 'title' && a?.title && b?.title) {
      return a?.title.localeCompare(b?.title);
    }
    if (sortBy === 'country') {
      return a?.country?.nameInt.localeCompare(b?.country?.nameInt);
    }
    return 0;
  });

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (filteredAttendeeList?.length > 100) {
      startTransition(() => {
        setSearchTerm(e.target.value);
      });
    } else {
      setSearchTerm(e.target.value);
    }
  };

  const handleSortBy = (sortBy: string) => {
    if (filteredAttendeeList?.length > 100) {
      startTransition(() => {
        setSortBy(sortBy);
      });
    } else {
      setSortBy(sortBy);
    }
  };

  const handleDisplayType = (type: string) => {
    if (filteredAttendeeList?.length > 100) {
      startTransition(() => {
        setDisplayType(type);
      });
    } else {
      setDisplayType(type);
    }
  };

  let colClassName = 'grid-cols-2';

  if (ref.current?.clientWidth < 768) {
    colClassName = 'grid-cols-1';
  } else if (ref.current?.clientWidth < 1024) {
    colClassName = 'grid-cols-3';
  } else if (ref.current?.clientWidth < 1280) {
    colClassName = 'grid-cols-4';
  } else {
    colClassName = 'grid-cols-5';
  }

  return (
    <div ref={ref} className="flex flex-col gap-y-4 w-full z-0">
      <div className="flex lg:items-center lg:flex-row flex-col lg:justify-start items-start gap-2">
        <div className="input w-full flex items-center  bg-white  relative rounded-md overflow-hidden">
          <i className="ri-search-2-line absolute top-1/2 left-3 transform text-sm -translate-y-1/2 font-normal text-slate-800"></i>
          <input className="w-full ml-6 font-normal text-sm h-[36px]" type="text" onChange={handleSearch} />
        </div>

        <SortByMenu sortBy={sortBy} onChange={handleSortBy} />
        <DisplayTypeToggle onChange={handleDisplayType} displayType={displayType} />
      </div>

      {isLoading || isPending ? (
        <LoadingSkeleton displayType={displayType} />
      ) : (
        <div
          className={cx(
            'w-full overflow-hidden  overflow-y-visble rounded-md ',
            (displayType === 'grid' || ref.current?.clientWidth < 768) && `grid ${colClassName} gap-4`,
            displayType === 'list' && ref.current?.clientWidth > 768 && 'flex flex-col divide-y divide-slate-200 rounded-md    list'
          )}
        >
          {sortedAttendeeList?.map((attendee: any) =>
            displayType === 'grid' || ref.current?.clientWidth < 768 ? (
              <AttendeeCard key={attendee.orderId} lang={lang} hideImage={hideImage} fields={fields} attendee={attendee} />
            ) : (
              <AttendeeListItem key={attendee.orderId} lang={lang} hideImage={hideImage} fields={fields} attendee={attendee} />
            )
          )}
        </div>
      )}
    </div>
  );
}

export default observer(AttendeeList);
